import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { throwError } from 'rxjs';
import {catchError, map } from 'rxjs/operators';
import { ISearchSection } from '@signature-it/ngx-generic/interfaces';
import { SolrRequestOptionsDTO, SolrRequestDTO } from '@signature-it/ngx-generic/models';
import { RestUrlsConfig, GenericConfig } from "@signature-it/ngx-generic/config";
import { LanguageService } from './language';

declare let _ : any;

@Injectable({
    providedIn: 'root'
})
export class SolrService {

    static readonly CATEGORIES_SEARCH_TYPE = 'categories';
    static readonly BRANDS_SEARCH_TYPE = 'brands';
    static readonly PRODUCTS_SEARCH_TYPE = 'products';
    static readonly FACET_SEARCH_TYPE = 'facet';
    protected getSolrUrl = `${this.config.appURL}/public/solr`;

    constructor(
        protected http: HttpClient,
		protected config: RestUrlsConfig,
        protected langSvc: LanguageService
    ) {}

    ngramRequest(searchText, searchSection: ISearchSection, count, fqs = [], langInField = true, ignore_session_variety = false , typeI = 1) {
        // Notice: if you need to add 1 more parameter to this function, consider change and use the ignore_session_variety parameter. We are not using it more. (and delete this comment after using it again).
        const request = new SolrRequestDTO();
        const currLang = this.langSvc.getCurrentLanguage();
        const searchInFields = searchSection.searchFields.map((s) => {
            if(s.includes('^')){
                return `${s}`;
            } else{
                return `${s}${langInField ? currLang.code : ''}_ngram`;
            }
        }).join(' ');
        if (searchSection.sortBy) {
            const sortFields = searchSection.sortBy.join(',');
            request.addQueryParameter(SolrRequestDTO.PARAMETER_SORT, sortFields);
        }
        count = searchSection.resultsNumber || count || GenericConfig.item_per_page[0];
        searchText = `${searchText}`;
        request.addQueryParameter(SolrRequestDTO.PARAMETER_ROWS, count);
        request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `lang_code_s:${currLang.code}`);
        request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `type_i:${typeI}`);
        request.addQueryParameter(SolrRequestDTO.PARAMETER_DEF_TYPE, 'edismax');
        request.addQueryParameter(SolrRequestDTO.PARAMETER_Q, encodeURIComponent(searchText));
        request.addQueryParameter(SolrRequestDTO.PARAMETER_QF, searchInFields);
        request.addQueryParameter(SolrRequestDTO.PARAMETER_Q_OP, 'AND');
        fqs.forEach(q => {
            let value = q.value instanceof Array ? q.value.join('" OR "'): q.value;
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `${q.key}:("${value}")`);
        });
        switch (searchSection.type) {
            case SolrService.PRODUCTS_SEARCH_TYPE:
                break;
            case SolrService.CATEGORIES_SEARCH_TYPE:
            case SolrService.BRANDS_SEARCH_TYPE:
                request.addQueryParameter(SolrRequestDTO.PARAMETER_GROUP, true);
                request.addQueryParameter(SolrRequestDTO.PARAMETER_GROUP_FIELD, searchSection.groupBy);
                break;
            case SolrService.FACET_SEARCH_TYPE:
                request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET, true);
                request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET_LIMIT, 2000);
                request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET_FIELD, `{!ex=${searchSection.facetField}}${searchSection.facetField}`);
                break;
        }
        if(searchSection.groupBy){
               request.addQueryParameter(SolrRequestDTO.PARAMETER_GROUP, true);
               request.addQueryParameter(SolrRequestDTO.PARAMETER_GROUP_FIELD, searchSection.groupBy);
        }
        return this.http.post(this.getSolrUrl, {  params: request.paramsToString(), extraQueryString: searchSection.extraQryString || ''});
       // return this.http.get(`${this.getSolrUrl}?${request.paramsToString()}${(searchSection.extraQryString || '')}`);
    }

    buildSolrUrl(availableFilters, options: SolrRequestOptionsDTO, isPromo, campaign) {
        const mainFacetField = options.taxonomy ? `${options.taxonomy}_cat_id_is` : `group_id_is`;
        const self = this;
        const request = new SolrRequestDTO();
        const currLang = this.langSvc.getCurrentLanguage();

        request.addQueryParameter(SolrRequestDTO.PARAMETER_ROWS, options.itemsPerPage);
        request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `lang_code_s:${currLang.code}`);
        request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET, true);
        request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET_LIMIT, 2000);
        request.addQueryParameter(SolrRequestDTO.PARAMETER_DEF_TYPE, 'edismax');

        if (options.product_type_s) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `product_type_s:${options.product_type_s}`);
        } else {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `${mainFacetField}:${options.groupId}`);
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `type_i:${options.type_i}`);
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET_FIELD, mainFacetField);
        }

        if (options.familyName) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `FamilyNameSlug_s:"${this.escapeHash(this.escapeLucene(options.familyName))}"`);
        }
        if(options.stats){
            request.addQueryParameter(SolrRequestDTO.PARAMETER_STATS, true);
        }
        if(options.statsField){
            request.addQueryParameter(SolrRequestDTO.PARAMETER_STATS_FIELD,`${this.escapeHash(this.escapeLucene(options.statsField))}`);
        }
        if(options.facet){
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET, true);
        }
        if(options.facetRange){
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET_RANGE,`${this.escapeHash(this.escapeLucene(options.facetRange))}`);
        }
        if(options.facetRangeStart){
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET_RANGE_START,`${options.facetRangeStart}`);
        }
        if(options.facetRangeEnd){
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET_RANGE_END,`${options.facetRangeEnd}`);
        }
        if(options.facetRangeGap){
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET_RANGE_GAP,`${this.escapeHash(this.escapeLucene(options.facetRangeGap))}`);
        }
        const solr_parameter_pivot = GenericConfig.solr_parameters && GenericConfig.solr_parameters['facet.pivot']? GenericConfig.solr_parameters['facet.pivot'] : '';
        const solr_parameter_pivot_array = solr_parameter_pivot.split(',');
        if (availableFilters && availableFilters.length > 0) {
          this.handleAvailableFilters(availableFilters, request, solr_parameter_pivot);
        }
        if (options.search) {
            let q = GenericConfig.searchExactMatch && options.search !== '*' ? `"${options.search}"` : `${options.search}`;
            request.addQueryParameter(SolrRequestDTO.PARAMETER_Q, encodeURIComponent(q));
            if (GenericConfig.solrSearchQf && GenericConfig.solrSearchQf[currLang.code]) {
                request.addQueryParameter(SolrRequestDTO.PARAMETER_QF, `${GenericConfig.solrSearchQf[currLang.code]}`);
            }
        }

        request.addQueryParameter(SolrRequestDTO.PARAMETER_START, ((options.pageNumber - 1) * options.itemsPerPage).toString());

        if(options.isGroupBy) {
            const groupBy = options.search ? options.groupByInSearch : options.groupByInCatalogue;
            if (groupBy) {
                request.addQueryParameter(SolrRequestDTO.PARAMETER_GROUP, true);
                request.addQueryParameter(SolrRequestDTO.PARAMETER_GROUP_NGROUPS, true);
                request.addQueryParameter(SolrRequestDTO.PARAMETER_GROUP_FIELD, groupBy);
            }
        }
        if (isPromo) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, 'is_promo_i:1');
        }
        if (campaign) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `Campaign_ss:"${campaign}"`);
        }
        if (options.properties) {
            _.forEach(options.properties, function(v, k) {
                request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `${k}:${v}`);
            });
        }
        if (GenericConfig.solr_parameters) {
            _.forEach(GenericConfig.solr_parameters, function(val, key) {
                let _val = val;
                if(key == 'facet.pivot') {
                    _val = `{!ex=${val}}${val}`;
                }
                if (_.isArray(val)) {
                    _.forEach(val, function(v, k) {
                        request.addQueryParameter(key, v);
                    });
                } else {
                    request.addQueryParameter(key, _val);
                }
            });
        }

        if (options.sort) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_SORT, options.sort);
        }
        if (options.bq) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_BQ, options.bq);
        }

        if (options.filters) {
            _.forEach(options.filters, function(val, key) {
                if (_.isArray(val)) {
                    const v = _.map(val, function(f) {
                        return `"${self.escapeLucene(f)}"`;
                    });
                    request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `{!tag=${key}}${key}:(${v.join(' OR ')})`);
                } else {
                    const regex = /\[.*TO.*\]/;
                    const value = regex.test(val) ? `{!tag=${key}}${key}:${val}` : `{!tag=${key}}${key}:"${self.escapeLucene(val)}"`;
                    request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, value);
                }
            });
        }
        const postData = {
          params: request.paramsToString(),
  	    extraQueryString: options.extraQryString || ''
        };
        return this.http.post(this.getSolrUrl, postData).pipe(
            map((res) => {
              if (options.taxonomy && res['facet_counts']?.['facet_fields']?.[mainFacetField]) {
                res['facet_counts']['facet_fields']['group_id_is'] = res['facet_counts']['facet_fields'][mainFacetField];
              }
              return res;
            }),
            catchError((error: any) => throwError(error))
        );
       /*return this.http.get(`${this.getSolrUrl}?${request.paramsToString()}${options.extraQryString}`).pipe(map((res) => {
            if (options.taxonomy && res['facet_counts']?.['facet_fields']?.[mainFacetField]) {
                res['facet_counts']['facet_fields']['group_id_is'] = res['facet_counts']['facet_fields'][mainFacetField];
            }
            return res;
        }), catchError((error: any) => throwError(error)));*/
    }

    escapeLucene(value) {
        if(!_.isString(value)) {
            return value;
        }
        value = value.replace(/&/g, '%26');
        value = value.replace(/\+/g, '%2B');
        // No idea why it needs lowercase b and not uppercase B over here
        var specials = ['b\\+', 'b\\-', '&', '!', '/', '(', ')', '{', '}', '[', ']', '^', /*'"',*/ '~', '*\\b', '?', ':', '\\', '"'];
        var regexp = new RegExp("(\\" + specials.join("|\\") + ")", "g");
        return value.replace(regexp, "\\$1");
    }

    escapeHash(string) {
        let escapeCharacters = "#";
        let charactersArray = string.split("");
        let parsedString = "";
        for(let character of charactersArray) {
            if(escapeCharacters.indexOf(character) === 0) {
                character = "%23";
            }
            parsedString += character
        }
        return parsedString;
    }

	handleAvailableFilters(availableFilters, request, solr_parameter_pivot) {
		availableFilters.forEach(filter => {
			if ((filter !== 'Name_ss') && (filter != 'FamilyName_s') && (filter !== 'FamilyName_ss')) {
				if(solr_parameter_pivot.includes(filter)) {
					request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET_FIELD, `{!ex=${solr_parameter_pivot}}${filter}`);
				} else {
					request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET_FIELD, `{!ex=${filter}}${filter}`);
				}
			}
		});
	}
}
